import { useEffect, useState, useMemo } from "react";
import { Skeleton, Dropdown, Drawer } from "../../components/ui";
import DataTableCards from "../../components/DataTable/DataTableCards";
import dayjs from "dayjs";
import swal from "../../utils/swal";
import {
  Search,
  Filter,
  Plus,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  NotebookPen,
  SearchX,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDeleteHomework, useGetAllHomework } from "../../query/homeworkQuery";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import {
  assigneeLabel,
  assigneeSummary,
  homeworkTypeLabel,
  snippet,
  teacherName,
  typeBadgeClass,
  HOMEWORK_TYPES,
  ASSIGNEE_TYPES,
} from "./homeworkHelpers";
import "../teachers/teachers.css";
import "./homework.css";
import FilterSelect from "../../components/FilterSelect";

function pageWindow(current, totalPages) {
  if (totalPages <= 3) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (current <= 2) return [1, 2, 3];
  if (current >= totalPages - 1) return [totalPages - 2, totalPages - 1, totalPages];
  return [current - 1, current, current + 1];
}

function Homework() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedAssignee, setSelectedAssignee] = useState("");
  const [paginationConfig, setPaginationConfig] = useState({
    pageNumber: 1,
    limit: "10",
    keyword: "",
    type: "",
    assignee: "",
  });
  const debouncedKeyword = useDebouncedValue(searchTerm.trim(), 400);

  const queryParams = useMemo(
    () => ({
      page: String(paginationConfig.pageNumber),
      limit: String(paginationConfig.limit),
      keyword: paginationConfig.keyword,
      type: paginationConfig.type,
      assignee: paginationConfig.assignee,
    }),
    [paginationConfig]
  );

  const { isLoading, isFetching, data: homeworkData } = useGetAllHomework(queryParams);
  const { mutate: deleteHomework } = useDeleteHomework();

  const docs = homeworkData?.docs || [];
  const pageSize = Number(paginationConfig.limit) || 10;
  const currentPage = Number(paginationConfig.pageNumber) || 1;
  const totalDocs = homeworkData?.totalDocs || 0;
  const totalPages = Math.max(1, Math.ceil(totalDocs / pageSize));
  const from = totalDocs === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalDocs);
  const hasFilters = Boolean(paginationConfig.type || paginationConfig.assignee);

  useEffect(() => {
    setPaginationConfig((prev) =>
      prev.keyword === debouncedKeyword ? prev : { ...prev, keyword: debouncedKeyword, pageNumber: 1 }
    );
  }, [debouncedKeyword]);

  useEffect(() => {
    if (!open) return;
    setSelectedType(paginationConfig.type || "");
    setSelectedAssignee(paginationConfig.assignee || "");
  }, [open, paginationConfig.type, paginationConfig.assignee]);

  const applyFilter = () => {
    setPaginationConfig((prev) => ({
      ...prev,
      pageNumber: 1,
      type: selectedType || "",
      assignee: selectedAssignee || "",
    }));
    setOpen(false);
  };

  const resetFilter = () => {
    setSelectedType("");
    setSelectedAssignee("");
    setPaginationConfig((prev) => ({
      ...prev,
      pageNumber: 1,
      type: "",
      assignee: "",
    }));
    setOpen(false);
  };

  const handleDelete = (item) => {
    swal({
      title: "Are you sure?",
      text: "Do you want to delete this homework entry?",
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then((isConfirm) => {
      if (!isConfirm) return;
      deleteHomework(item._id, {
        onSuccess: () => swal("Success", "Homework deleted successfully", "success"),
        onError: (error) => swal("Cannot delete", error?.message || "Failed to delete homework", "error"),
      });
    });
  };

  return (
    <div className="data-page homework-page">
      <div className="data-toolbar">
        <div className="data-toolbar-search">
          <label className="data-search">
            <Search size={16} />
            <input
              placeholder="Search here..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {isFetching && searchTerm ? <span className="data-search-spinner" aria-hidden /> : null}
          </label>
          <button
            type="button"
            className={`data-icon-btn${hasFilters ? " is-active" : ""}`}
            aria-label="Filter"
            onClick={() => setOpen(true)}
          >
            <Filter size={18} />
            {hasFilters ? <span className="data-filter-dot" /> : null}
          </button>
        </div>

        <div className="data-toolbar-actions">
          <button type="button" className="data-primary" onClick={() => navigate("/homework/add")}>
            <Plus size={18} />
            New Homework
          </button>
        </div>
      </div>

      <div className="data-card">
        {isLoading && !homeworkData ? (
          <div style={{ padding: 24 }}>
            <Skeleton active />
          </div>
        ) : docs.length === 0 ? (
          <div className="data-empty">
            <span className="data-empty-icon">
              {paginationConfig.keyword || hasFilters ? (
                <SearchX size={36} strokeWidth={1.6} />
              ) : (
                <NotebookPen size={36} strokeWidth={1.6} />
              )}
            </span>
            <h3>{paginationConfig.keyword || hasFilters ? "No homework found" : "No homework yet"}</h3>
            <p>
              {paginationConfig.keyword || hasFilters
                ? "Try a different keyword or clear filters."
                : "Add homework to see it listed here."}
            </p>
          </div>
        ) : (
          <>
            <DataTableCards fetching={isFetching}>
              <table className="data-table hw-table">
                <thead>
                  <tr>
                    <th className="hw-col-title">Title</th>
                    <th className="hw-col-type">Type</th>
                    <th className="hw-col-assignee">Assignee</th>
                    <th className="hw-col-target">Target</th>
                    <th className="hw-col-teacher">Teacher</th>
                    <th className="hw-col-due">Due Date</th>
                    <th className="hw-col-action">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((item) => {
                    const actionItems = [
                      {
                        key: "1",
                        label: <div style={{ padding: "8px 12px" }}>View</div>,
                        onClick: () => navigate(`/homework/${item._id}`),
                      },
                      {
                        key: "2",
                        label: <div style={{ padding: "8px 12px" }}>Edit</div>,
                        onClick: () => navigate(`/homework/edit/${item._id}`),
                      },
                      {
                        key: "3",
                        label: <div style={{ padding: "8px 12px", color: "#e83038" }}>Delete</div>,
                        onClick: () => handleDelete(item),
                      },
                    ];

                    return (
                      <tr key={item._id}>
                        <td className="hw-col-title">
                          <p className="data-name hw-title">{item.title || "—"}</p>
                          <p className="hw-desc-snippet">{snippet(item.description)}</p>
                        </td>
                        <td className="hw-col-type">
                          <span className={`data-pill hw-type-badge ${typeBadgeClass(item.type)}`}>
                            {homeworkTypeLabel(item.type)}
                          </span>
                        </td>
                        <td className="hw-col-assignee">{assigneeLabel(item.assignee)}</td>
                        <td className="hw-col-target">
                          <div className="hw-target-cell">
                            <strong>{assigneeSummary(item)}</strong>
                          </div>
                        </td>
                        <td className="hw-col-teacher">{teacherName(item.teacherDoc)}</td>
                        <td className="hw-col-due">
                          {item.dueDate ? dayjs(item.dueDate).format("MMM D, YYYY") : "—"}
                        </td>
                        <td className="hw-col-action">
                          <Dropdown menu={{ items: actionItems }} trigger={["click"]} placement="bottomRight">
                            <button type="button" className="data-action" aria-label="More actions">
                              <MoreHorizontal size={20} />
                            </button>
                          </Dropdown>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </DataTableCards>

            <div className="data-pager">
              <p className="data-pager-text">
                Showing {from}-{to} from {totalDocs} data
              </p>
              <div className="data-pages">
                <button
                  type="button"
                  className="data-page-btn is-nav"
                  disabled={currentPage <= 1}
                  onClick={() =>
                    setPaginationConfig((prev) => ({ ...prev, pageNumber: Math.max(1, currentPage - 1) }))
                  }
                >
                  <ChevronLeft size={18} />
                </button>
                {pageWindow(currentPage, totalPages).map((page) => (
                  <button
                    key={page}
                    type="button"
                    className={`data-page-btn${page === currentPage ? " is-active" : ""}`}
                    onClick={() => setPaginationConfig((prev) => ({ ...prev, pageNumber: page }))}
                  >
                    {page}
                  </button>
                ))}
                <button
                  type="button"
                  className="data-page-btn is-nav"
                  disabled={currentPage >= totalPages}
                  onClick={() =>
                    setPaginationConfig((prev) => ({
                      ...prev,
                      pageNumber: Math.min(totalPages, currentPage + 1),
                    }))
                  }
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        placement="right"
        title="Filter"
        width={400}
        footer={
          <>
            <button type="button" className="bw-drawer-btn is-ghost" onClick={resetFilter}>
              Clear All
            </button>
            <button type="button" className="bw-drawer-btn is-primary" onClick={applyFilter}>
              Apply
            </button>
          </>
        }
      >
        <div className="bw-filter-field">
          <p className="bw-filter-label">Type</p>
          <FilterSelect
            multiple
            value={selectedType}
            onChange={setSelectedType}
            options={[{ value: "", label: "All types" }, ...HOMEWORK_TYPES]}
          />
        </div>
        <div className="bw-filter-field">
          <p className="bw-filter-label">Assignee</p>
          <FilterSelect
            multiple
            value={selectedAssignee}
            onChange={setSelectedAssignee}
            options={[{ value: "", label: "All assignees" }, ...ASSIGNEE_TYPES]}
          />
        </div>
      </Drawer>
    </div>
  );
}

export default Homework;
